import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MessageSquareText, ChevronDown } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import * as conversationsApi from '@/api/conversations.api';
import * as asuntosApi from '@/api/asuntos.api';
import { HojaDelAsunto } from '@/components/athena/HojaDelAsunto';
import { MedioDelMensaje } from '@/components/athena/MedioDelMensaje';
import { esSoloMarcador } from '@/components/athena/marcadores';
import { hora12, enlaceWhatsApp } from '@/lib/athena';

/**
 * EL CHAT — SOLO LECTURA.
 *
 * Regla dura del mandato: *"No hay caja de escribir."* Esta pantalla no tiene
 * una, y no es un descuido que haya que arreglar después: es la razón por la
 * que existe separada de la bandeja de Conversia vendedor.
 *
 * En su lugar va "Responder en WhatsApp", que **abre** el chat en el teléfono.
 * La app nunca envía y nunca marca como visto.
 *
 * ═══ EL MENSAJE RESALTADO ═══
 *
 * Se llega acá desde "Ver mensaje" de un asunto, con `?mensaje=<id>`. Ese
 * mensaje se resalta dos segundos y se centra en pantalla. Es lo que hace
 * cierto el *"todo se puede rastrear hasta el mensaje exacto"*: sin el
 * resaltado, el dueño cae en un chat de 200 mensajes y tiene que buscar.
 */

export default function ChatDeAthena() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const [abierto, setAbierto] = useState<string | null>(null);
  const [verResumen, setVerResumen] = useState(true);
  const resaltado = params.get('mensaje');
  const refResaltado = useRef<HTMLDivElement | null>(null);

  const conversacion = useQuery({
    queryKey: ['conversacion', id],
    queryFn: () => conversationsApi.getConversation(id!),
    enabled: Boolean(id),
  });

  const mensajes = useQuery({
    queryKey: ['mensajes', id],
    queryFn: () => conversationsApi.listMessages(id!, { take: 200 }),
    enabled: Boolean(id),
  });

  const asuntos = useQuery({
    queryKey: ['asuntos', 'chat', id],
    queryFn: () => asuntosApi.getAsuntos({ limite: 200 }),
    enabled: Boolean(id),
  });

  // Centrar el mensaje del que salió el asunto. Sin esto, "Ver mensaje" deja
  // al dueño arriba de un chat largo, buscando a mano lo que vino a ver.
  useEffect(() => {
    if (!resaltado || !refResaltado.current) return;
    refResaltado.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [resaltado, mensajes.data]);

  const lista = mensajes.data?.items ?? [];
  const deEsteChat = (asuntos.data?.asuntos ?? []).filter((a) => a.conversationId === id);
  const contacto = conversacion.data?.contact;
  const esGrupo = contacto?.isGroup === true;
  const nombre = contacto?.name?.trim() || contacto?.pushName?.trim() || contacto?.phone || 'Chat';
  const wa = enlaceWhatsApp(contacto?.phone ?? null);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => navegar(-1)}
          aria-label="Atrás"
          className="grid h-11 w-11 place-items-center rounded-md text-slate-600 dark:text-slate-300"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-lg font-semibold text-slate-900 dark:text-slate-50">
          {nombre}
        </h1>
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer">
            <Button size="sm">
              <MessageSquareText className="h-4 w-4" /> Responder en WhatsApp
            </Button>
          </a>
        )}
      </div>

      {/* ── ATHENA EN ESTE CHAT ── */}
      {deEsteChat.length > 0 && (
        <Card>
          <CardBody className="space-y-1">
            <button
              onClick={() => setVerResumen((v) => !v)}
              className="flex w-full items-center justify-between text-sm font-semibold text-slate-700 dark:text-slate-200"
            >
              Athena en este chat ({deEsteChat.length})
              <ChevronDown className={cn('h-4 w-4 transition', !verResumen && '-rotate-90')} />
            </button>
            {verResumen &&
              deEsteChat.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setAbierto(a.id)}
                  className="block w-full min-h-[40px] py-1 text-left text-sm text-slate-600 dark:text-slate-300"
                >
                  · {a.descripcion}
                </button>
              ))}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="space-y-2">
          {mensajes.isPending ? (
            <Spinner />
          ) : lista.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">No hay mensajes.</p>
          ) : (
            [...lista].reverse().map((m) => {
              const mio = m.role !== 'CLIENT';
              const esEse = resaltado === m.id;
              return (
                <div
                  key={m.id}
                  ref={esEse ? refResaltado : undefined}
                  className={cn('flex', mio ? 'justify-end' : 'justify-start')}
                >
                  <div
                    className={cn(
                      'max-w-[80%] space-y-1 rounded-2xl px-3 py-2 text-sm transition',
                      mio
                        ? 'bg-brand-600 text-white'
                        : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
                      esEse && 'ring-2 ring-amber-400',
                    )}
                  >
                    {/* EN GRUPOS, QUIEN HABLA.
                        Sin esto, un grupo de 15 es una pila de burbujas iguales
                        y no se sabe quien dijo que — que es justo lo que uno
                        entra a ver. Solo en los ajenos: los propios ya se
                        distinguen por el lado y el color. */}
                    {!mio && esGrupo && m.senderName && (
                      <p className="text-[11px] font-semibold text-brand-600 dark:text-brand-300">
                        {m.senderName}
                      </p>
                    )}

                    <MedioDelMensaje mensaje={m} mio={mio} />

                    {/* El texto SOLO si dice algo. Con una foto sin epigrafe, el
                        adaptador deja "[Imagen]" como contenido: pintarlo debajo
                        de la imagen ya visible es ruido. */}
                    {m.content && !esSoloMarcador(m.content) && (
                      <p className="whitespace-pre-wrap break-words">{m.content}</p>
                    )}

                    <p className={cn('text-[11px]', mio ? 'text-white/70' : 'text-slate-400')}>
                      {hora12(m.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </CardBody>
      </Card>

      {/* Donde iría la caja de escribir. Se dice por qué no está: si no, se lee
          como una pantalla a medio hacer. */}
      <div className="rounded-lg border border-dashed border-slate-300 p-3 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
        Athena solo lee. Para responder, abre el chat en WhatsApp.
        {wa && (
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="ml-1 text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
          >
            Abrir
          </a>
        )}
      </div>

      <HojaDelAsunto asuntoId={abierto} onCerrar={() => setAbierto(null)} />
    </div>
  );
}
